/**
 * Le contenu du jeu, côté client — SPEC-CONTENU §1.1, arbitrage A25.
 *
 * Le moteur est **pur** : il ne lit aucun fichier, on lui passe le contenu en paramètre.
 * En mode « bac à sable local » (aucun serveur, aucun réseau), le client importe donc les
 * `content/*.json` en dur — Vite les inline au build — et les **valide avec les mêmes
 * schémas Zod que le serveur** (`@maxime/shared` ré-exporte `content/schema.js`, qui est
 * pur ; `content/load.js` ne l'est pas, il lit le disque, et n'est donc pas importable ici).
 *
 * **Le jour où le serveur arrive**, seul ce fichier change : `chargerContenu()` devient un
 * `await fetch("/api/content")`. Aucun composant ne dépend de la façon dont le contenu entre.
 */

import {
  AxeProduitFile,
  BoutiqueFile,
  CommentsFile,
  EquipeFile,
  EventsFile,
  filtrerSkillsInterrupteurs,
  FreelancesFile,
  GuideFile,
  ManagersFile,
  MetaFile,
  MilestonesFile,
  RivauxFile,
  SkillsFile,
  SubjectsFile,
  TrendsFile,
  type BoutiqueObjet,
  type CandidatManager,
  type Commentaire,
  type EngineContent,
  type Freelance,
  type GameEvent,
  type Milestone,
  type Rival,
  type Skill,
  type Subject,
  type Trend,
} from "@maxime/shared";

import brutAxeProduit from "@content/axe-produit.json";
import brutBoutique from "@content/boutique.json";
import brutComments from "@content/comments.json";
import brutEvents from "@content/events.json";
import brutFreelances from "@content/freelances.json";
import brutGuide from "@content/guide.json";
import brutManagers from "@content/managers.json";
import brutMeta from "@content/meta.json";
import brutMilestones from "@content/milestones.json";
import brutProducteur from "@content/producteur.json";
import brutRivaux from "@content/rivaux.json";
import brutSkills from "@content/skills.json";
import brutSubjects from "@content/subjects.json";
import brutTrends from "@content/trends.json";

/**
 * Le contenu validé. Satisfait `EngineContent` (contrat A25) et porte en plus ce dont
 * l'interface a besoin pour ÉCRIRE des textes : noms de sujets, libellés de choix, nœuds de
 * l'arbre. Le moteur, lui, n'en lit que les quatre collections qu'il connaît.
 */
export interface ContenuJeu extends EngineContent {
  readonly subjects: readonly Subject[];
  readonly trends: readonly Trend[];
  readonly skills: readonly Skill[];
  readonly events: readonly GameEvent[];
  readonly milestones: readonly Milestone[];
  /**
   * ⟦A-11 §4⟧ Les 17 objets de la boutique — le matériel de VIE.
   *
   * Il manquait, et son absence n'était pas anodine : `EngineContent.boutique` est optionnel,
   * donc `acheter_objet` refusait **tout** achat en `objet_inconnu` sans que rien ne le dise.
   * La boutique moteur existait, son contenu aussi, et le bac à sable ne les présentait pas
   * l'un à l'autre.
   */
  readonly boutique: readonly BoutiqueObjet[];
  /**
   * ⟦A-15 §3 / M3.16⟧ Le vivier de créateurs PNJ — **le rival qui monte pendant que tu dors**.
   *
   * Il manquait ici, et son absence se voyait à l'écran par un trou : le serveur charge
   * `rivaux.json` (`shared/content/load.ts`) et tire donc 2-3 PNJ au bootstrap, mais le client
   * n'avait aucun moyen de les NOMMER — `rivauxLisibles(state, contenu())` rendait une liste
   * vide, puisqu'elle écarte tout PNJ dont le contenu ne porte plus la définition. Côté bac à
   * sable, c'était pire encore : `tirerRivaux` ne trouvait aucun vivier, et la mécanique
   * n'existait tout simplement pas.
   */
  readonly rivaux: readonly Rival[];
  /**
   * ⟦SPEC-FIL · M3.23⟧ **Les 87 commentaires du Fil**, et leur absence était une exemption
   * DATÉE (`parite-client.test.ts`) : tant que `fil_actif` valait `false`, aucun commentaire
   * n'arrivait ni côté serveur ni côté client, donc le bac à sable ne perdait rien.
   *
   * L'interrupteur passe à `true` dans ce même commit — l'exemption tombe avec lui. Sans cet
   * import, le Fil serait **vide en bac à sable et plein sur un compte** : précisément le bug
   * de `rivaux.json` ⟦A-32⟧, le plus coûteux du projet.
   *
   * Le client en a besoin pour deux choses que le moteur ne fait jamais : afficher le **texte**
   * et l'**auteur** (le moteur ne manipule que des ids), et savoir quelles **réponses** un
   * commentaire autorise (§8.2) — sans quoi l'écran proposerait des boutons que `resolve`
   * refuse.
   */
  readonly comments: readonly Commentaire[];
  /**
   * ⟦A-44 · A-71⟧ **Le guide MeTube** — le faux journal débloqué au nœud gardé à 3 000 abonnés.
   *
   * Il est importé ici pour la raison qui a coûté le plus cher à ce projet ⟦A-32⟧ : le serveur
   * charge `guide.json` (`shared/content/load.ts`), donc un guide présent côté compte et absent
   * en bac à sable serait exactement le même bug que `rivaux.json` — une mécanique vivante d'un
   * côté, morte de l'autre, et aucun test pour le dire.
   *
   * ⚠️ Le moteur ne le reçoit jamais : `ContenuJeu` étend `EngineContent`, mais **ce champ-là
   * n'en fait pas partie**. Le journal ne peut donc pas déplacer une partie, par construction.
   */
  readonly guide: GuideFile;
  /**
   * Le méta-contenu, **au type que le schéma produit** — plus une forme recopiée à la main.
   *
   * ⚠️ Elle était écrite ici en dur (`{ content_version: string }`), et ⟦A-57⟧ l'a cassée sans
   * que personne ne s'en aperçoive avant la CI : le Fil a ajouté `fil_actif` à `MetaFile` et à
   * `EngineContent.meta`, et deux formes littérales sans **aucune propriété commune** ne
   * s'assignent pas. Le symptôme accusait huit fichiers ; la cause tenait en une ligne.
   *
   * 🔑 Au-delà du correctif : le client doit **lire l'interrupteur**, sinon le bac à sable ne
   * verrait jamais le Fil s'allumer — c'est le maillon « contenu → écran » sans lequel une
   * mécanique existe dans le moteur et nulle part ailleurs.
   */
  readonly meta: MetaFile;
  /**
   * ⟦SPEC-AXE-PRODUIT §9.2 / A-62⟧ **Les mots de l'axe Produit ↔ Authentique.**
   *
   * Le moteur rend un nombre et une bande, jamais un texte (SPEC-MOTEUR §9.2) : sans ce
   * fichier, l'interface saurait que la chaîne est « artisanale » et n'aurait aucun mot pour
   * le dire. Il est importé ici parce qu'⟦A-32⟧ a montré ce qui arrive sinon — `rivaux.json`
   * chargé côté serveur et absent du bac à sable, donc une mécanique qui n'existait pas du
   * tout pour un joueur hors compte, sans qu'aucun test ne dise rien.
   */
  readonly axeProduit: AxeProduitFile;
  /**
   * ⟦A-4 E / M2.10 · A-66⟧ **Le vivier du marché des freelances** — 25 personnes, 5 par étape.
   *
   * Son absence était une exemption DATÉE (`parite-client.test.ts`), et elle coûtait exactement
   * ce qu'⟦A-32⟧ avait déjà coûté avec `rivaux.json` : le marché existait côté serveur et
   * **n'existait pas en bac à sable**, où `offresPourEtape` rendait une liste vide et
   * `embaucheValide` refusait toute embauche en `indisponible`. Un joueur hors compte qui
   * achetait *Le Studio* (8 PC, 2 000 ₵) n'obtenait rien du tout, en silence.
   *
   * Le client en a besoin pour deux choses que le moteur ne fait jamais : **nommer** les gens
   * (le moteur ne manipule que des ids) et afficher le **prix** avant l'embauche
   * (`devisEmbauches`) — la donnée que la délégation transitoire prélevait en silence.
   */
  readonly freelances: readonly Freelance[];
  /**
   * ⟦SPEC-PRODUCTEUR §11.4 / A-66⟧ Les prix et les mots de l'équipe. Importé **en même temps**
   * que le socle moteur, et pas « quand l'écran arrivera » : c'est précisément le décalage qui
   * a produit les deux ruptures ci-dessus.
   */
  readonly equipe: EquipeFile;
  /**
   * ⟦C8 · A-120⟧ Le vivier des managers recrutables (`content/managers.json`) — importé en
   * même temps que le reste du socle équipe, pas « quand l'écran de recrutement arrivera » :
   * même précédent qu'`equipe` lui-même (⟦A-32⟧, la classe de bug la plus coûteuse du projet).
   */
  readonly managers: readonly CandidatManager[];
  /** Index par id — l'UI fait beaucoup de « donne-moi le nom de ce sujet ». */
  readonly parSujet: ReadonlyMap<string, Subject>;
  readonly parTendance: ReadonlyMap<string, Trend>;
  readonly parSkill: ReadonlyMap<string, Skill>;
  readonly parEvenement: ReadonlyMap<string, GameEvent>;
  readonly parObjet: ReadonlyMap<string, BoutiqueObjet>;
  readonly parRival: ReadonlyMap<string, Rival>;
  /** ⟦SPEC-FIL §1.2⟧ Le Fil ne porte que des ids : c'est ici qu'on retrouve texte et auteur. */
  readonly parCommentaire: ReadonlyMap<string, Commentaire>;
  /** ⟦M2.10⟧ Le marché ne porte que des ids : c'est ici qu'on retrouve nom, phrase et cote. */
  readonly parFreelance: ReadonlyMap<string, Freelance>;
  /** ⟦C8 · A-120⟧ Le vivier des managers ne porte que des ids : nom, style, prix. */
  readonly parManager: ReadonlyMap<string, CandidatManager>;
}

function indexer<T extends { id: string }>(items: readonly T[]): ReadonlyMap<string, T> {
  return new Map(items.map((item) => [item.id, item]));
}

/**
 * Valide et indexe le contenu. Lève une erreur lisible au boot si `content/` est cassé —
 * exactement comme le serveur refuse de démarrer (SPEC-API §8.2), en beaucoup plus court :
 * le client n'a pas à réimplémenter le rapport de violations, il n'est pas autoritatif.
 */
function construire(): ContenuJeu {
  const meta = MetaFile.parse(brutMeta);
  const subjects = SubjectsFile.parse(brutSubjects);
  const trends = TrendsFile.parse(brutTrends);
  const skills = SkillsFile.parse(brutSkills);
  const events = EventsFile.parse(brutEvents);
  const milestones = MilestonesFile.parse(brutMilestones);
  const boutique = BoutiqueFile.parse(brutBoutique);
  const axeProduit = AxeProduitFile.parse(brutAxeProduit);
  const rivaux = RivauxFile.parse(brutRivaux);
  const comments = CommentsFile.parse(brutComments);
  const freelances = FreelancesFile.parse(brutFreelances);
  const equipe = EquipeFile.parse(brutProducteur);
  const managers = ManagersFile.parse(brutManagers);
  const guide = GuideFile.parse(brutGuide);

  // ⟦SPEC-PRODUCTEUR §9 / A-66⟧ Le MÊME filtre que `shared/src/content/load.ts`, et il doit le
  // rester : deux loaders qui ne s'accordent pas sur ce que contient l'arbre, c'est ⟦A-32⟧ —
  // la mécanique vivante sur un compte et absente en bac à sable, sans qu'aucun test ne parle.
  const skillsVisibles = filtrerSkillsInterrupteurs(skills, meta);

  return {
    meta,
    axeProduit,
    subjects,
    trends,
    skills: skillsVisibles,
    events,
    milestones,
    boutique,
    rivaux,
    comments,
    guide,
    freelances,
    equipe,
    producteur: equipe,
    managers,
    parSujet: indexer(subjects),
    parTendance: indexer(trends),
    parSkill: indexer(skillsVisibles),
    parEvenement: indexer(events),
    parObjet: indexer(boutique),
    parRival: indexer(rivaux),
    parCommentaire: indexer(comments),
    parFreelance: indexer(freelances),
    parManager: indexer(managers),
  };
}

let cache: ContenuJeu | null = null;

/** Le contenu validé, construit une seule fois par session de navigateur. */
export function contenu(): ContenuJeu {
  cache ??= construire();
  return cache;
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦SPEC-API §5.10 / SPEC-UI §1.6 · A-81⟧ `GET /api/content` — la dérive, détectée
// ─────────────────────────────────────────────────────────────────────────────

/** L'ETag de la dernière lecture de `/api/content` — pour ne pas retélécharger ce qui n'a pas bougé. */
const CLE_ETAG_CONTENU = "maxime.contenu.etag.v1";

export interface DeriveContenu {
  /** Le semver éditorial du serveur (`meta.content_version` de SON `content/`). */
  serveur: string;
  /** Celui que cette page a embarqué au build. */
  client: string;
}

/**
 * **Ce que le client fait de `GET /api/content`, et ce qu'il n'en fait pas — dit en clair.**
 *
 * Le contenu reste embarqué au build (`construire()`), et ce n'est pas un oubli : le moteur du
 * bac à sable en a besoin sans réseau, et la route ne sert aujourd'hui que cinq fichiers sur
 * treize (sans `boutique`, `rivaux`, `comments`, `freelances`, `producteur`, `guide`,
 * `axe-produit`, `meta`) et retire `potentiel_evergreen` des sujets — le schéma `SubjectsFile`
 * la refuserait telle quelle. Basculer la SOURCE du contenu sur la route demande un schéma
 * public des sujets et une route qui sert tout : c'est un lot à part, signalé dans le recompte.
 *
 * Ce que la route permet **dès maintenant**, et qui manquait : savoir si **le serveur et cette
 * page jouent avec le même contenu**. Un client déployé avant le serveur (ou un onglet resté
 * ouvert pendant un déploiement) affiche des textes, des nœuds et des prix qui ne sont plus
 * ceux que le moteur applique — la classe de bug la plus coûteuse du projet ⟦A-32⟧, mais entre
 * deux versions au lieu de deux loaders. L'ETag évite de retélécharger le bundle quand rien n'a
 * bougé depuis la dernière visite (`304`).
 *
 * Rend la dérive, ou `null` (même version, `304`, pas de réseau, bac à sable). Ne lève jamais :
 * c'est une vérification de confort, pas une porte.
 */
export async function verifierContenuServeur(): Promise<DeriveContenu | null> {
  let etagConnu: string | null = null;
  try {
    etagConnu = localStorage.getItem(CLE_ETAG_CONTENU);
  } catch {
    etagConnu = null;
  }
  try {
    const { appelApiConditionnel } = await import("../api/http");
    const r = await appelApiConditionnel<{ content_version: string; content_semver?: string }>("/content", etagConnu);
    if (r.statut === 304) return null;
    try {
      if (r.etag) localStorage.setItem(CLE_ETAG_CONTENU, r.etag);
    } catch {
      // Sans stockage, on revérifiera à la prochaine visite — ce n'est pas grave.
    }
    const serveur = r.corps.content_semver ?? r.corps.content_version;
    const client = contenu().meta.content_version;
    return serveur === client ? null : { serveur, client };
  } catch {
    return null;
  }
}
